import { IConsumerDataResponse } from '../../consumerUxlApis/types/consumerUXlResponse.types';
import { Item, Order } from '../../consumerUxlApis/types/orderDetails.types';
import { SessionData } from '../../session';

export function getReservationUXLKeys(consumerData: IConsumerDataResponse | undefined, sessionData: SessionData) {
  const reservationKeys: Partial<Record<string, string | boolean | number>> = Object.seal({
    res_isRedemption: '',
    res_rate_is_member_rate: '',
    res_rate_program: '',
    res_rooms_per_night: '',
    res_stay_length: '',
    res_revenue_total_locale: '',
    res_room_rate_tab: '',
    res_total_points_for_stay: '',
    res_cash_points_mix: '',
    res_room_nights_total: '',
    res_num_adults: '',
    res_num_children: '',
    res_stay_nights: '',
  });

  const orderDetails = consumerData?.reservationConfirmation?.order;

  const populateKeysFromReservationSession = (sessionData: SessionData) => {
    const availabilityData = sessionData?.data?.AriesSearch?.searchCriteria?.availabilityRequestVO;
    if (availabilityData) {
      const numRooms = availabilityData.numRooms;
      const stayLength = availabilityData.lengthOfStay;
      reservationKeys['res_rooms_per_night'] = numRooms?.toString() || '';
      reservationKeys['res_stay_length'] = stayLength && stayLength > 0 ? stayLength.toString() : '';
      if (stayLength && stayLength > 0 && numRooms) {
        reservationKeys['res_room_nights_total'] = (numRooms * stayLength).toString() || '';
        reservationKeys['res_stay_nights'] = (numRooms * stayLength).toString() || '';
      }
      reservationKeys['res_num_adults'] = availabilityData.numAdultsPerRoom?.toString() || '';
      reservationKeys['res_num_children'] = availabilityData.numChildrenPerRoom?.toString() || '';
    }
    const reservationData = sessionData?.data?.AriesReservation;
    if (reservationData?.availabilityRequestVO) {
      reservationKeys['res_isRedemption'] =
        reservationData?.availabilityRequestVO?.rewardsRedemption?.toString() ?? 'false';
    }
    reservationKeys['res_rate_is_member_rate'] = reservationData?.isMemberRate?.toString() ?? '';
    const rateTypeCode = reservationData?.selectedRateType;
    const rateCategoryTypeCode = reservationData?.selectedRateCategoryType;
    if (rateTypeCode == 'standard') {
      reservationKeys['res_room_rate_tab'] = 'Rate Tab:Standard Rates';
    } else if (rateTypeCode == 'packages') {
      reservationKeys['res_room_rate_tab'] = 'Rate Tab:Room Packages';
    } else if (rateTypeCode == 'special-rates' || rateCategoryTypeCode == 'product.rate.type.category.special') {
      reservationKeys['res_room_rate_tab'] = 'Rate Tab:Special Rates';
    } else if (rateTypeCode == 'redemption') {
      reservationKeys['res_room_rate_tab'] = 'Rate Tab:Redeem Points';
    } else if (rateTypeCode == 'exclusive') {
      reservationKeys['res_room_rate_tab'] = 'Rate Tab:Marriott Rewards Offers';
    } else if (rateTypeCode == 'prepay') {
      reservationKeys['res_room_rate_tab'] = 'Rate Tab:Prepay and Save';
    }
  };

  const populateKeysFromOrderDetails = (orderDetails: Order) => {
    const item: Item | undefined = orderDetails?.items?.[0];
    const basicInfo = item?.basicInformation;
    reservationKeys['res_isRedemption'] = basicInfo?.isRedemption ?? false;

    if (basicInfo?.isRedemption === true) {
      reservationKeys['res_rate_is_member_rate'] = 'true';
    }

    const rateCode = basicInfo?.product?.rateDetails?.[0]?.marketCode ?? '';
    if (rateCode.length > 4) {
      reservationKeys['res_rate_program'] = rateCode.substring(rateCode.length - 4);
    } else {
      reservationKeys['res_rate_program'] = rateCode;
    }

    const numberOfRooms = item?.property?.basicInformation?.numberOfRooms ?? 0;
    reservationKeys['res_rooms_per_night'] = String(numberOfRooms);
    const lengthOfStay = basicInfo?.product?.basicInformation?.lengthOfStay ?? 0;
    reservationKeys['res_stay_length'] = String(lengthOfStay);
    const totalPrice = item?.totalPricing?.rateAmounts?.[0]?.amount?.origin?.amount ?? 0;
    if (totalPrice < 0) {
      reservationKeys['res_revenue_total_locale'] = String(totalPrice);
    } else if (totalPrice > 0 && sessionData?.data?.AriesReservation?.selectedRateCategoryType !== 'redemption') {
      reservationKeys['res_revenue_total_locale'] = '';
    }

    const rewardsNumber = item?.guests?.[0]?.rewardsNumber ?? '';
    reservationKeys['res_total_points_for_stay'] = String(rewardsNumber);

    const isCashRate = basicInfo?.product?.totalPricing?.rateAmounts?.[0]?.amount?.origin?.amount;
    const isPointsRate = basicInfo?.product?.totalPricing?.rateAmounts?.[0]?.points;
    const isCashAndPointsRate = isCashRate && isPointsRate;
    if (
      sessionData?.data?.AriesReservation?.shoppingCartId !== null ||
      sessionData?.data?.AriesReservation?.tripId !== ''
    ) {
      if (isCashAndPointsRate) {
        reservationKeys['res_cash_points_mix'] = 'Cash+Points';
      } else if (isCashRate) {
        reservationKeys['res_cash_points_mix'] = 'Cash';
      } else if (isPointsRate) {
        reservationKeys['res_cash_points_mix'] = 'Points';
      }
    }
  };

  populateKeysFromReservationSession(sessionData);
  if (orderDetails && orderDetails.items) {
    populateKeysFromOrderDetails(orderDetails);
  }

  const filteredKeys = Object.fromEntries(
    Object.entries(reservationKeys).filter(([_, value]) => value != null && value !== '')
  );
  return { ...filteredKeys };
}
